import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'
import calc from 'pared/Routes/renderer/utils/calc'
import { DATE_FORMAT } from 'pared/data/getDateRanges'
import { toUsdStr } from 'pared/utils/number'

interface ICogsSummaryNodeType {
  annualOpportunityCost: number
  opportunityCost: number
  totalNetSales: number
  annualTotalNetSales: number
  endingInventory: number
}

interface ICogsSummaryType {
  cogsSummary: {
    nodes: ICogsSummaryNodeType[]
  }
}

const query = gql`
  query cogsSummary($iLocationId: Int!, $iStartDate: Date!, $iEndDate: Date!) {
    cogsSummary(
      iLocationId: $iLocationId
      iStartDate: $iStartDate
      iEndDate: $iEndDate
    ) {
      nodes {
        annualOpportunityCost
        opportunityCost
        totalNetSales
        annualTotalNetSales
        endingInventory
      }
    }
  }
`

const useBbbFoodCost = () => {
  const startDate = moment().subtract(30, 'days').format(DATE_FORMAT)
  const endDate = moment().subtract(1, 'day').format(DATE_FORMAT)
  const { groupFilter } = useGroupFilter()
  const locationId = groupFilter?.ids[0] || 0
  const locationName = groupFilter?.label.replace(/\d+ -/, '')
  const { data, loading } = useQuery<ICogsSummaryType>(query, {
    variables: {
      iLocationId: locationId,
      iStartDate: startDate,
      iEndDate: endDate,
    },
    skip: !groupFilter,
  })

  return {
    data: useMemo(() => {
      if (!data) return null

      const cogsData = data.cogsSummary?.nodes[0]
      const percent = cogsData?.endingInventory / cogsData?.totalNetSales
      const message = (() => {
        if (percent >= 0.5) return 'higher than 50%'
        if (percent >= 0.4) return 'higher than 40%'
        if (percent >= 0.3) return 'higher than 30%'
        return null
      })()

      return {
        title: 'Food Cost',
        total: calc(
          cogsData?.annualOpportunityCost > 0
            ? cogsData?.annualOpportunityCost
            : null,
          '/',
          12,
        ),
        detail: message
          ? `For the trailing 30 days, ${locationName}'s ending Inventory % of Net Sales is ${message}.
             This store holds ${toUsdStr(
               calc(cogsData.annualOpportunityCost, '/', 12) as number,
             )} more inventory than required each week.`
          : `${locationName} is doing well with its ending inventory`,
      }
    }, [locationName, data]),
    loading,
  }
}

export default useBbbFoodCost
